/**
 * Form Access API
 *
 * HTTP wrapper for Step 3 form access submission.
 * Communicates with backend POST /api/form-access endpoint.
 */

import { API_URLS } from '../constants/apiUrls';
import { FormAccessRequestData, FormAccessResponseData } from '../types/api.types';

/**
 * Submit form access request to backend.
 *
 * Success path: backend returns application/pdf → file is downloaded directly,
 * returns { success: true, downloaded: true, fileName }.
 * Error path: backend returns JSON → mapped to FormAccessResponseData as before.
 */
export async function submitFormAccess(
  request: FormAccessRequestData,
  sessionToken: string,
  formAccessId?: string | null
): Promise<FormAccessResponseData> {
  const response = await fetch(API_URLS.generateForm, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${sessionToken}`,
      ...(formAccessId ? { 'X-Form-Access-Id': formAccessId } : {}),
    },
    body: JSON.stringify(request)
  });

  const contentType = response.headers.get('content-type') || '';

  // ── PDF response: trigger browser download directly ────────────────────
  if (response.ok && contentType.includes('application/pdf')) {
    const blob = await response.blob();

    // Extract filename from Content-Disposition header
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^";\n]+)"?/i);
    const fileName = match ? match[1] : '1095Form.pdf';

    // Programmatically trigger Save As dialog
    const url = window.URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, downloaded: true, fileName, message: '' };
  }

  // ── JSON response: success or error ────────────────────────────────────
  let data: FormAccessResponseData;
  try {
    data = await response.json() as FormAccessResponseData;
  } catch {
    throw {
      status: response.status,
      data: {
        success: false,
        message: `Server error: ${response.status} ${response.statusText}`,
        error: 'INVALID_RESPONSE'
      } as FormAccessResponseData
    };
  }

  if (!response.ok) {
    throw { status: response.status, data };
  }

  return data;
}
